import { TranslateFunc } from "utils/translation.utils";
import { OptionsObject, SnackbarKey, SnackbarMessage, useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiErrorCodes } from "../../../../api/api-error-codes";
import { getErrorCode, getErrorStatusCode } from "../../../../api/api-response";
import { replaceUriParameter, RestEndpoints } from "../../../../api/endpoints";
import getInstance from "../../../../utils/rest";
import { ICfmCreateResponseField } from "../../../domain/routing-assignment/cfm-response-field";
import { CfmQueryKeys } from "../cfm-query-keys";

const updateResponseField = async (id: number, responseField: ICfmCreateResponseField): Promise<void> => {
  const url = replaceUriParameter(RestEndpoints.ResponseField, "id", id);
  await getInstance().put(url, { ...responseField });
};

export type EnqueueSnackbarFunc = (message: SnackbarMessage, options?: OptionsObject | undefined) => SnackbarKey;
export const handleError = (error: unknown, enqueueSnackbar: EnqueueSnackbarFunc, t: TranslateFunc) => {
  const errorMsg = getErrorCode(error);
  const errorCode = getErrorStatusCode(error);
  if (errorMsg === ApiErrorCodes.CfmInternalNumberAlreadyExists.toString()) {
    enqueueSnackbar(t("responsefields.already_exists"), { variant: "error" });
  } else {
    enqueueSnackbar(t("general.error_occurred", { errorCode, errorMsg }), { variant: "error" });
  }
};

export const useUpdateResponseFieldQuery = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({

    mutationFn: ({ id, responseField }: { id: number; responseField: ICfmCreateResponseField }) =>
      updateResponseField(id, responseField),

    onError: (error) => {
      handleError(error, enqueueSnackbar, t);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CfmQueryKeys.GetAllResponseFieldsInfinite] });
    },
  });
};
